import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { loadCatalog, computeActiveIndices, parseFiltersFromSearch, buildPathTokenFrequency, renderHighlightedPath, getBestUrl } from '../lib/data'
import { ThumbnailGrid } from '../components/ThumbnailGrid'
import type { LoadedData } from '../lib/data'
import type { KV } from '../lib/types'

function normalizeConfig(config?: KV[]) {
  const map: Record<string, any> = {}
  if (config && Array.isArray(config)) {
    for (const kv of config) {
      // Normalize key format: ensure consistent Title Case
      let normalizedKey: string
      if (kv.key.includes('_')) {
        // Convert snake_case to Title Case
        normalizedKey = kv.key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
      } else if (kv.key.includes(' ')) {
        // Ensure each word is capitalized for space-separated keys
        normalizedKey = kv.key.replace(/\b\w/g, l => l.toUpperCase())
      } else {
        // Single word - capitalize first letter
        normalizedKey = kv.key.charAt(0).toUpperCase() + kv.key.slice(1).toLowerCase()
      }
      map[normalizedKey] = kv.value
    }
  }
  return map
}

function hashConfig(map: Record<string, any>): string {
  const keys = Object.keys(map).sort()
  const obj: Record<string, any> = {}
  for (const k of keys) obj[k] = map[k]
  const s = JSON.stringify(obj)
  let h = 2166136261 >>> 0
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619)
  return (h >>> 0).toString(16)
}

export default function ConfigPage() {
  const { search } = useLocation()
  const nav = useNavigate()
  const params = new URLSearchParams(search)
  const cfg = params.get('cfg') || ''
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<LoadedData | null>(null)
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('grid')  // Default to grid view
  const filters = useMemo(() => parseFiltersFromSearch(search), [search])

  useEffect(() => { loadCatalog().then(setData).catch(e => setError(String(e))).finally(() => setLoading(false)) }, [])

  const active = useMemo(() => (data ? computeActiveIndices(data, filters) : []), [data, filters])

  const entriesByType = useMemo(() => {
    if (!data) return { runs: [], artifacts: [], displayOrder: [] as string[], displayMap: {} as Record<string, any> }

    // Get actively filtered features - same logic as ExplorePage
    const activeFilterKeys = Object.keys(filters).filter(k => {
      const filterSet = filters[k as keyof typeof filters]
      return filterSet && filterSet.size > 0
    })

    // Find representative order and map for the cfg hash
    let displayOrder: string[] = []
    let displayMap: Record<string, any> = {}
    const allActive = active.map(i => data.all[i])
    
    // Use same grouping logic as ExplorePage
    for (const e of allActive) {
      const configMap = normalizeConfig(e.config)
      const groupingMap = { ...configMap }
      
      // Add actively filtered features to distinguish configs that differ on filtered dimensions
      // For multi-value features (arrays), only track which filter values match, not the full array
      for (const filterKey of activeFilterKeys) {
        if (e.features) {
          const featureValue = e.features.find((f: any) => f.key === filterKey)?.value
          if (featureValue !== undefined && featureValue !== null) {
            // If it's an array and we're filtering it, only include the matching filter values
            if (Array.isArray(featureValue) && filters[filterKey as keyof typeof filters]) {
              const filterSet = filters[filterKey as keyof typeof filters]
              if (filterSet) {
                const matchingValues = featureValue.filter(v => filterSet.has(v)).sort()
                if (matchingValues.length > 0) {
                  groupingMap[`_filter_${filterKey}`] = matchingValues.join(',')
                }
              }
            } else {
              groupingMap[`_filter_${filterKey}`] = featureValue
            }
          }
        }
      }
      
      if (hashConfig(groupingMap) === cfg) {
        if (e.config && Array.isArray(e.config)) {
          // Normalize display keys too
          displayOrder = e.config.map(kv => {
            if (kv.key.includes('_')) {
              return kv.key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
            } else if (kv.key.includes(' ')) {
              return kv.key.replace(/\b\w/g, l => l.toUpperCase())
            } else {
              return kv.key.charAt(0).toUpperCase() + kv.key.slice(1).toLowerCase()
            }
          })
        }
        displayMap = configMap // Use original config for display, not grouping key
        break
      }
    }

    const runs = [] as typeof data.runs
    const artifacts = [] as typeof data.artifacts

    for (const e of allActive) {
      const configMap = normalizeConfig(e.config)
      const groupingMap = { ...configMap }
      
      // Add actively filtered features 
      // For multi-value features (arrays), only track which filter values match, not the full array
      for (const filterKey of activeFilterKeys) {
        if (e.features) {
          const featureValue = e.features.find((f: any) => f.key === filterKey)?.value
          if (featureValue !== undefined && featureValue !== null) {
            // If it's an array and we're filtering it, only include the matching filter values
            if (Array.isArray(featureValue) && filters[filterKey as keyof typeof filters]) {
              const filterSet = filters[filterKey as keyof typeof filters]
              if (filterSet) {
                const matchingValues = featureValue.filter(v => filterSet.has(v)).sort()
                if (matchingValues.length > 0) {
                  groupingMap[`_filter_${filterKey}`] = matchingValues.join(',')
                }
              }
            } else {
              groupingMap[`_filter_${filterKey}`] = featureValue
            }
          }
        }
      }
      
      if (hashConfig(groupingMap) !== cfg) continue
      if (data.runs.some(r => r.path === e.path)) runs.push(e)
      else artifacts.push(e)
    }

    const byPathAsc = (a: any, b: any) => a.path.localeCompare(b.path)
    runs.sort(byPathAsc)
    artifacts.sort(byPathAsc)
    return { runs, artifacts, displayOrder, displayMap }
  }, [data, active, cfg, filters])

  // Build token frequency maps separately for runs and artifacts
  const runsPaths = useMemo(() => entriesByType.runs.map(e => e.path), [entriesByType.runs])
  const artifactsPaths = useMemo(() => entriesByType.artifacts.map(e => e.path), [entriesByType.artifacts])
  const runsTokenFreq = useMemo(() => buildPathTokenFrequency(runsPaths, 'runs'), [runsPaths])
  const artifactsTokenFreq = useMemo(() => buildPathTokenFrequency(artifactsPaths, 'artifacts'), [artifactsPaths])

  if (loading) return <div className="app">Loading…</div>
  if (error) return <div className="app" style={{ color: 'crimson' }}>Error: {error}</div>
  if (!data) return null

  return (
    <div className="app">
      <div className="header"><div className="title">Config</div></div>
      <div className="mono" style={{ marginBottom: 16 }}>
        {entriesByType.displayOrder.map(k => (
          <div key={k}><span className="code-inline">{k}</span>: <span>{String(entriesByType.displayMap[k])}</span></div>
        ))}
      </div>
      
      {/* View mode toggle for artifacts section */}
      <div className="view-controls" style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
        <button 
          className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-ghost'}`}
          onClick={() => setViewMode('list')}
        >
          <span style={{ marginRight: 6 }}>☰</span> List View
        </button>
        <button 
          className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-ghost'}`}
          onClick={() => setViewMode('grid')}
        >
          <span style={{ marginRight: 6 }}>⊞</span> Grid View
        </button>
      </div>
      
      <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 400px', minWidth: 0 }}>
          <h3>Runs ({entriesByType.runs.length})</h3>
          <ul style={{ listStyle: 'none', padding: 0 }}>
            {entriesByType.runs.map(e => (
              <li key={e.path} style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <span className="code-inline" style={{ flex: '1 1 200px', minWidth: 0, wordBreak: 'break-all' }}>{renderHighlightedPath(e.path, runsTokenFreq, runsPaths.length)}</span>
                  <button className="btn btn-ghost" onClick={() => nav({ pathname: '/view', search: `${search}${search ? '&' : '?'}path=${encodeURIComponent(e.path)}` })}>View</button>
                  <a className="download-link" href={getBestUrl(e.path, true)} download>Download</a>
                </div>
              </li>
            ))}
          </ul>
        </div>
        
        <div style={{ flex: '1 1 400px', minWidth: 0 }}>
          <h3>Artifacts ({entriesByType.artifacts.length})</h3>
          
          {viewMode === 'list' ? (
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {entriesByType.artifacts.map(e => (
                <li key={e.path} style={{ marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <span className="code-inline" style={{ flex: '1 1 200px', minWidth: 0, wordBreak: 'break-all' }}>{renderHighlightedPath(e.path, artifactsTokenFreq, artifactsPaths.length)}</span>
                    <button className="btn btn-ghost" onClick={() => nav({ pathname: '/view', search: `${search}${search ? '&' : '?'}path=${encodeURIComponent(e.path)}` })}>View</button>
                    <a className="download-link" href={getBestUrl(e.path, true)} download>Download</a>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <ThumbnailGrid 
              items={entriesByType.artifacts}
              onItemClick={(item) => nav({ pathname: '/view', search: `${search}${search ? '&' : '?'}path=${encodeURIComponent(item.path)}` })}
            />
          )}
        </div>
      </div>
    </div>
  )
}


